import { html, View } from 'rune-ts';
import klass from './ProductRankingBody.module.scss';
import { Tabs } from '../../../../components/cells/Tabs/Tabs';
import { ProductListApiS } from '../../../../../features/ProductList/outbound/share';
import { CateListTypeS } from '../../../../../features/CateList/outbound/share';
import { bestProductTypeMap, type ProductCardViewData } from '../../../../../features/ProductList/type';
import { BaseLayoutOptions } from '../../../../../features/Core/service';
import { ProductRankingCoverBannerView } from '../ProductRankingBannerView/ProductRankingCoverBanner';
import { ProductRankingQueryParams, validateRankType } from '../../utils/validate';
import { delay } from '@fxts/core';
import { QueryController } from '../../../../../shared/util/QueryController';
import { ExcludeSoldOutInput } from '../ExcludeSoldOutInput';
import { MarppleShopLoader } from '@services/MarppleShop/shared/client/loader.ts';
import { SectionTabs } from '@services/MarppleShop/shared/components/atoms/SectionTabs/SectionTabs.ts';
import { SectionTitle } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle.ts';
import {
  CategoryLevel1_CODE,
  ICategory,
} from '../../../../../../../shared/mps-shared/domain/category/types.ts';
import { CateListWithRankingView } from '@services/MarppleShop/renderApp/components/cells/CateListWithRanking/CateListWithRankingView.ts';
import { createBuilderByCateRootCode } from '@services/MarppleShop/features/ProductList/util';
import { CateRootGnbMoView } from '@services/MarppleShop/renderApp/components/cells/CateRootGnbMo/CateRootGnbMoView.ts';
import { DomainBannerView } from '@services/MarppleShop/renderApp/pages/Main/views/DomainBanner/DomainBanner.ts';
import { BannerTypeS } from '@services/MarppleShop/features/Banner/outbound/share.ts';
import { CardProductControllerView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CardProductControllerView.ts';
import { DefaultProductListEmptyScreen } from '@services/MarppleShop/renderApp/components/cells/ProductCardList/ProductCardListEmptyScreen.ts';
import { classes } from '@services/MarppleShop/shared/util';

type ProductRankingBodyData = {
  readonly best_products: ProductCardViewData[];
  readonly updated_at: Date | null;
  readonly recent_product?: ProductCardViewData;
  readonly cate_lists: CateListTypeS.LegacyCateList[];
  readonly layout_options: BaseLayoutOptions;
  readonly cate_root_code: CategoryLevel1_CODE;
  readonly cate_root: ICategory;
  readonly query: {
    cate_list_id?: number;
  };
  readonly selected_cate_list_code?: string;
  readonly banners: BannerTypeS.MainBannerData[];
};

export type ProductRankingPageData = ProductRankingBodyData & {
  readonly query: ProductRankingQueryParams;
};

export class ProductRankingBody extends View<ProductRankingBodyData> {
  private readonly i18n = {
    exclude_sold_out: ET('mps2::product::filter::without_sold_out'),
    exclude_sold_out_short: ET('mps2::product::filter::without_sold_out_short'),
  };

  private readonly queryController: QueryController<ProductRankingQueryParams>;

  private readonly coverBannerView = new ProductRankingCoverBannerView({
    is_mobile: this.is_mobile,
    last_updated_at: this.data.updated_at,
  });

  private readonly excludeSoldOutCheckboxView: ExcludeSoldOutInput;

  private readonly sectionTabsView: SectionTabs;
  private readonly sectionTitleView: SectionTitle;

  constructor(data: ProductRankingPageData) {
    super(data);

    this.queryController = new QueryController(data.query, ['rank_type', 'exclude_sold_out', 'cate_list_id']);

    this.excludeSoldOutCheckboxView = new ExcludeSoldOutInput({
      name: 'exclude_sold_out',
      label: this.i18n.exclude_sold_out_short,
      is_mobile: this.is_mobile,
      active: this.queryController.getQuery().exclude_sold_out,
    });
    this.sectionTabsView = new SectionTabs({
      tabs: [
        {
          id: bestProductTypeMap.realtime,
          label: this.getLabelByRankType(bestProductTypeMap.realtime),
          href: this._makeSectionTitleBuilder().setRankType('REALTIME').build(),
          preventDefault: true,
        },
        {
          id: bestProductTypeMap.weekly,
          label: this.getLabelByRankType(bestProductTypeMap.weekly),
          href: this._makeSectionTitleBuilder().setRankType('WEEKLY').build(),
          preventDefault: true,
        },
        {
          id: bestProductTypeMap.monthly,
          label: this.getLabelByRankType(bestProductTypeMap.monthly),
          href: this._makeSectionTitleBuilder().setRankType('MONTHLY').build(),
          preventDefault: true,
        },
      ],
      current_tab_id: this.queryController.getQuery().rank_type,
      size: 'small',
      variant: this.is_mobile ? 'bubble' : undefined,
    });

    this.sectionTitleView = new SectionTitle({
      title_props: {
        title: this.sectionTabsView,
        size: data.layout_options.is_mobile ? 'xSmall' : 'small',
      },
    });
  }

  private getLabelByRankType(rank_type: string) {
    const is_selected = this.queryController.getQuery().rank_type == rank_type;

    if (rank_type == bestProductTypeMap.weekly) {
      return is_selected ? ET('mps2::ranking::weekly_best') : ET('mps2::ranking::weekly');
    }
    if (rank_type == bestProductTypeMap.monthly) {
      return is_selected ? ET('mps2::ranking::monthly_best') : ET('mps2::ranking::monthly');
    }
    return is_selected ? ET('mps2::ranking::realtime_best') : ET('mps2::ranking::realtime');
  }

  private _makeSectionTitleBuilder() {
    return createBuilderByCateRootCode(this.data.cate_root_code);
  }

  protected override template({ best_products, cate_root_code }: ProductRankingBodyData) {
    return html`
      <div class="${klass.body}">
        ${this.is_mobile ? new CateRootGnbMoView({ cate_root_code: this.data.cate_root_code }) : ''}
        <!-- 배너 -->
        ${this.data.banners.length > 2
          ? html`<div class="${klass.banners}">
              ${new DomainBannerView({ banners: this.data.banners, is_mobile: this.is_mobile })}
            </div>`
          : ''}
        <!-- 배너아래 -->
        <div class="${klass.section}">
          <!-- Title -->
          <div class="${klass.title_section}">
            <!-- 중카테고리 -->
            <div class="${klass.cate_lists}">
              ${new CateListWithRankingView(
                {
                  cate_root: this.data.cate_root,
                  cate_root_code: this.data.cate_root_code,
                  is_ranking_page: true,
                  selected_cate_list_code: this.data.selected_cate_list_code,
                  without_sold_out: true,
                },
                {
                  is_mobile: this.is_mobile,
                },
              )}
            </div>

            <!-- 실시간, 주간, 월간 Tabs -->
            <div class="${klass.tabs_wrap}">
              <div>${this.sectionTitleView}</div>
              <div class="${klass.checkbox_wrap}">${this.excludeSoldOutCheckboxView}</div>
            </div>
          </div>

          <!-- 상품 목록 -->
          <div class="${classes(klass.product_list, { [klass.empty]: best_products.length === 0 })}">
            ${best_products.length > 0
              ? best_products.map(
                  (bestProduct) =>
                    new CardProductControllerView({
                      card_product_view_props: {
                        type:
                          cate_root_code === CategoryLevel1_CODE.GOODS ||
                          cate_root_code === CategoryLevel1_CODE.HANDMADE
                            ? 'E'
                            : 'F',
                        size: this.is_mobile ? 'Medium' : 'Large',
                        layout: 'Vertical',
                        theme: 'Light',
                      },
                      product_card_view_data: bestProduct,
                      options: {
                        resize_width: 600,
                      },
                    }),
                )
              : new DefaultProductListEmptyScreen()}
          </div>
        </div>
      </div>
    `;
  }

  protected override onRender() {
    if (this.is_mobile) {
      this.subView(Tabs)?.scrollIntoView({ block: 'nearest', inline: 'center' });
    }

    // 품절 상품 제외 변경
    this.delegate(ExcludeSoldOutInput.InputChangedEvent, ExcludeSoldOutInput, async (e) => {
      this.queryController
        .setQuery({
          exclude_sold_out: e.detail,
        })
        .syncQuery();

      await this.updateProductList();
    });

    // 실시간, 월간, 주간 변경
    this.delegate(SectionTabs.ChangeTabEvent, SectionTabs, async (e) => {
      this.queryController
        .setQuery({
          rank_type: validateRankType(e.detail.tabId),
        })
        .syncQuery();

      this.sectionTabsView.tabViews.forEach((tab) => {
        tab.setLabel(this.getLabelByRankType(tab.getId())).redraw();
      });

      await this.updateProductList();
    });
  }

  private get is_mobile(): boolean {
    return this.data.layout_options.is_mobile;
  }

  private async updateProductList() {
    const fetchPromise = ProductListApiS.getBestProductListApi({
      ...this.queryController.getQuery(),
      cate_root_id: this.data.cate_root.id,
      include_watcher_count: true,
      limit: 100,
    });

    // 200ms 이내로 들어오면 loading 없이 보여주기
    const result = await Promise.race([fetchPromise, delay(200, 'loading')]);

    if (result === 'loading') {
      MarppleShopLoader.start();
    }

    const response = await fetchPromise;

    // 상품 목록 갱신
    const container = this.element().querySelector(`.${klass.product_list}`);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    container!.classList.toggle(klass.empty, response.products.length === 0);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    container!.replaceChildren(
      ...(response.products.length > 0
        ? response.products.map((product) =>
            new CardProductControllerView({
              card_product_view_props: {
                type:
                  this.data.cate_root_code === CategoryLevel1_CODE.GOODS ||
                  this.data.cate_root_code === CategoryLevel1_CODE.HANDMADE
                    ? 'E'
                    : 'F',
                size: this.is_mobile ? 'Medium' : 'Large',
                layout: 'Vertical',
                theme: 'Light',
              },
              product_card_view_data: product,
              options: {
                resize_width: 600,
              },
            }).render(),
          )
        : [new DefaultProductListEmptyScreen().render()]),
    );

    // 마지막 업데이트 시간 갱신
    this.coverBannerView.setUpdatedAt(response.updated_at);
    this.coverBannerView.redraw();

    MarppleShopLoader.end();
  }
}
